import Vue from 'vue';
import Router from 'vue-router';

Vue.use(Router);

function refresh(promise) {
  return promise.catch(() => {
    // asnyc chunk could not be loaded
    if (!window.location.href.match(/moduleError/)) {
      window.location.href = `${window.location.href}?moduleError=1`;
    }
    // you likely want to add some kind of failsafe i.e. saving&checking a timestamp
    // to prevent endless reload loops if something is seriously wrong with your deployment
    // or the user's connection
  });
}

function createRouter() {
  const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior(to) {
      if (to.hash) {
        return { selector: to.hash };
      }
      return {};
    },
    routes: [
      {
        path: '/',
        component: () => refresh(import('./views/Home.vue')),
      },
      {
        path: '/app/:type/:id',
        component: () => refresh(import('./views/App.vue')),
      },
      {
        name: 'search',
        path: '/search',
        component: () => refresh(import('./views/Search.vue')),
      },
      {
        path: '/news/:id?',
        component: () => refresh(import('./views/News.vue')),
      },
      {
        path: '/requests',
        name: 'requests',
        component: () => refresh(import('./views/Requests.vue')),
      },
      {
        path: '/link',
        component: () => refresh(import('./views/Link')),
      },
      {
        path: '/link/confirm',
        component: () => refresh(import('./views/Link/Confirm.vue')),
      },
      {
        path: '/banned',
        component: () => refresh(import('./views/Banned.vue')),
      },
      {
        path: '/redirect/:ticket',
        component: () => refresh(import('./views/Redirect.vue')),
      },
      {
        name: 'ban',
        path: '/ban',
        component: () => refresh(import('./views/Banned.vue')),
        props: true,
      },
      {
        name: 'success',
        path: '/success',
        component: () => refresh(import('./views/Success.vue')),
        props: true,
      },
      {
        name: 'error',
        path: '/error',
        component: () => refresh(import('./views/Error.vue')),
        props: true,
      },
      {
        path: '/my',
        redirect: '/my/devices',
        component: () => refresh(import('./views/My')),
        children: [
          {
            path: 'devices',
            component: () => refresh(import('./views/My/Devices.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'status',
            component: () => refresh(import('./views/My/Status.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'ipa-cache',
            component: () => refresh(import('./views/My/IPACache.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'updates',
            component: () => refresh(import('./views/My/Updates.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'configure',
            component: () => refresh(import('./views/My/Configure.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'unlink',
            component: () => refresh(import('./views/My/Unlink.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'store',
            component: () => refresh(import('./views/My/Store.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'dylibs',
            component: () => refresh(import('./views/My/Dylibs.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'repos',
            component: () => refresh(import('./views/My/Repos.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'repos/import',
            redirect: to => ({ path: '/repos/import', query: to.query }),
          },
          {
            path: 'error',
            component: () => refresh(import('./views/My/Error.vue')),
            meta: {
              authRequired: false,
            },
          },
          {
            path: 'buy',
            component: () => refresh(import('./views/My/Buy.vue')),
            meta: {
              authRequired: false,
            },
          },
          {
            path: 'pro',
            component: () => refresh(import('./views/My/Pro.vue')),
            meta: {
              authRequired: false,
            },
          },
          {
            path: 'plus',
            component: () => refresh(import('./views/My/Plus.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'roulette',
            component: () => refresh(import('./views/My/Roulette.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'referrals',
            component: () => refresh(import('./views/My/Referrals.vue')),
            meta: {
              authRequired: true,
            },
          },
          {
            path: 'p2p-certificates',
            component: () => refresh(import('./views/My/P2PCertificates.vue')),
          },
        ],
      },
      {
        path: '/repos',
        component: () => refresh(import('./views/Repos')),
      },
      {
        path: '/repos/import',
        component: () => refresh(import('./views/Repos/Import.vue')),
      },
      {
        path: '/repos/:id',
        component: () => refresh(import('./views/Repos/Repo.vue')),
      },
      {
        path: '/repos/:repoId/app/:id',
        component: () => refresh(import('./views/App.vue')),
      },
      {
        path: '/info/api',
        component: () => refresh(import('./views/Info/Api.vue')),
      },
      {
        path: '/info/privacy',
        component: () => refresh(import('./views/Info/Privacy.vue')),
      },
      {
        path: '/info/tos',
        component: () => refresh(import('./views/Info/Tos.vue')),
      },
      {
        path: '/info/about',
        component: () => refresh(import('./views/Info/About.vue')),
      },
      {
        path: '/info/empire-strikes-back',
        component: () => refresh(import('./views/Info/Empire.vue')),
      },
      {
        path: '/info/programs',
        component: () => refresh(import('./views/Info/Programs.vue')),
      },
      {
        path: '/articles/:id',
        component: () => refresh(import('./views/Article.vue')),
      },
      {
        path: '/plus-required',
        component: () => refresh(import('./views/PlusRequired.vue')),
      },
      {
        name: '404',
        path: '*',
        redirect: {
          name: 'error',
          params: {
            message: 'We were unable to find such page.',
            link: '/',
            linkText: 'GO_TO_MAIN_PAGE',
          },
        },
      },
    ],
  });

  router.beforeEach((to, from, next) => {
    const toProfile = to.matched[0].path === '/my';
    const { authRequired } = to.meta;

    if (Vue.store.getters.devicesLoaded
        && !Vue.store.getters.isDeviceLinked
        && toProfile && authRequired
    ) {
      next({ path: '/my/error' });
    } else {
      next();
    }
  });

  router.afterEach((to, from) => {
    if (to.name === 'search' && from.name === 'search') {
      return;
    }

    try {
      const content = document.getElementById('content');
      const padding = parseFloat(window.getComputedStyle(content, null).getPropertyValue('padding-top'));

      if (content.getBoundingClientRect().top + padding < 0) {
        window.scrollTo(0, 0);
      }
    } catch {
      // eslint-disable-next-line no-empty
    }
  });

  return router;
}

export default createRouter;
